import React, { useEffect, useState, useCallback } from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Flex,
  Group,
  Loader,
  NativeSelect,
  Paper,
  Table,
  Text,
  createStyles,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import swal from "sweetalert";
import { https } from "../../http";
import { getCookie } from "../../utils";

// ── Code color system ────────────────────────────────────────────
const CODE_META = [
  { value: "0", label: "0 – Independent", name: "Independent", bg: "#EAF3DE", color: "#3B6D11" },
  { value: "1", label: "1 – Supervision", name: "Supervision", bg: "#E1F5EE", color: "#0F6E56" },
  { value: "2", label: "2 – Limited Assistance", name: "Limited Assistance", bg: "#E6F1FB", color: "#185FA5" },
  { value: "3", label: "3 – Extensive Assistance", name: "Extensive Assistance", bg: "#FAEEDA", color: "#854F0B" },
  { value: "4", label: "4 – Totally Dependent", name: "Totally Dependent", bg: "#FCEBEB", color: "#A32D2D" },
];

const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_LABELS = ["M", "T", "W", "Th", "F", "Sa", "Su"];

// ── Styles ───────────────────────────────────────────────────────
const useStyles = createStyles(() => ({
  wrapper: {
    fontFamily: "Inter, sans-serif",
  },
  table: {
    "& thead th": {
      fontSize: 12,
      fontWeight: 600,
      textTransform: "uppercase",
      color: "#666",
      padding: "8px 6px",
      background: "#F5F0E8",
      borderBottom: "2px solid #E0D9CC",
    },
    "& tbody tr": {
      "&:nth-of-type(even)": { background: "#FAFAF5" },
      "&:hover .remove-btn": { opacity: 1 },
    },
    "& td": { padding: "6px 4px", verticalAlign: "middle" },
  },
  dayCell: {
    width: 30,
    textAlign: "center",
  },
  dayCheckbox: {
    cursor: "pointer",
    width: 24,
    height: 24,
    borderRadius: 4,
    border: "2px solid #D1CBC0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.15s ease",
    margin: "0 auto",
    "&:hover": { borderColor: "#E6A817" },
  },
  dayActive: {
    background: "#E6A817",
    borderColor: "#E6A817",
    color: "#FFF",
  },
  confirmBar: {
    background: "#FEF2F2",
    borderTop: "1px solid #FCA5A5",
    padding: "10px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
}));

// ── Default: single blank activity row ───────────────────────────
const DEFAULT_ACTIVITIES = [
  { activity: "New Activity", code: 0, days: { mon: true, tue: true, wed: true, thu: true, fri: true, sat: true, sun: true } },
];

// ── Main component ───────────────────────────────────────────────
const AdlMatrixEditor = ({ clientId, facilityId, clientName, facilityName, readOnly = false, onSaved }) => {
  const { classes, cx } = useStyles();
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pendingRemove, setPendingRemove] = useState(null);

  const token = getCookie("token");
  const api = useCallback(() => https(token), [token]);

  // ── Fetch ────────────────────────────────────────────────
  const fetchMatrix = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api().get(`/adl-matrix/${clientId}?facility=${facilityId}`);
      setMatrix(res?.data?.matrix || null);
    } catch (e) {
      console.error("[AdlMatrix] fetch error:", e?.response?.data || e?.message);
    } finally {
      setLoading(false);
    }
  }, [clientId, facilityId, api]);

  useEffect(() => {
    if (clientId && facilityId) fetchMatrix();
  }, [clientId, facilityId, fetchMatrix]);

  // ── Create matrix with defaults ────────────────────────────
  const handleCreate = async () => {
    try {
      setSaving(true);
      const res = await api().post("/adl-matrix", {
        client: clientId,
        facility: facilityId,
        activities: DEFAULT_ACTIVITIES,
      });
      setMatrix(res?.data?.matrix);
      swal("Created", "ADL Matrix created", "success");
      if (onSaved) onSaved();
    } catch (e) {
      swal("Error", e?.response?.data?.message || e?.message, "error");
    } finally {
      setSaving(false);
    }
  };

  // ── Save full matrix ─────────────────────────────────────
  const handleSave = async () => {
    if (!matrix?._id) return;
    try {
      setSaving(true);
      const activeActivities = (matrix.activities || [])
        .filter((a) => a.status === "active")
        .map(({ _id, activity, code, days, notes, order }) => {
          const row = { activity, code, days, notes, order };
          // Only include _id if it's a real MongoDB id (not a temp one)
          if (_id && !String(_id).startsWith("temp_")) row._id = _id;
          return row;
        });
      const res = await api().put(`/adl-matrix/${matrix._id}`, {
        activities: activeActivities,
      });
      setMatrix(res?.data?.matrix);
      swal("Saved", "ADL Matrix updated successfully", "success");
      if (onSaved) onSaved();
    } catch (e) {
      swal("Error", e?.response?.data?.message || e?.message, "error");
    } finally {
      setSaving(false);
    }
  };

  // ── Add activity (local only, saved on "Save matrix") ─────
  const handleAddActivity = () => {
    if (!matrix) return;
    const maxOrder = (matrix.activities || []).reduce(
      (max, a) => Math.max(max, a.order || 0),
      -1
    );
    const newAct = {
      _id: `temp_${Date.now()}`,
      activity: "New Activity",
      code: 0,
      days: { mon: true, tue: true, wed: true, thu: true, fri: true, sat: true, sun: true },
      notes: "",
      order: maxOrder + 1,
      status: "active",
    };
    setMatrix((prev) => ({
      ...prev,
      activities: [...(prev.activities || []), newAct],
    }));
  };

  // ── Archive / Restore ────────────────────────────────────
  const handleArchive = async (activityId) => {
    // If it's a temp activity (not yet saved), just remove locally
    if (String(activityId).startsWith("temp_")) {
      setMatrix((prev) => ({
        ...prev,
        activities: prev?.activities?.filter((a) => a._id !== activityId),
      }));
      setPendingRemove(null);
      return;
    }
    try {
      const res = await api().patch(
        `/adl-matrix/${matrix._id}/activity/${activityId}/archive`
      );
      setMatrix(res?.data?.matrix);
      setPendingRemove(null);
    } catch (e) {
      swal("Error", e?.response?.data?.message || e?.message, "error");
    }
  };

  const handleRestore = async (activityId) => {
    try {
      const res = await api().patch(
        `/adl-matrix/${matrix._id}/activity/${activityId}/restore`
      );
      setMatrix(res?.data?.matrix);
    } catch (e) {
      swal("Error", e?.response?.data?.message || e.message, "error");
    }
  };

  // ── Local state updates ──────────────────────────────────
  const updateActivity = (activityId, field, value) => {
    setMatrix((prev) => ({
      ...prev,
      activities: prev?.activities?.map((a) =>
        a._id === activityId ? { ...a, [field]: value } : a
      ),
    }));
  };

  const toggleDay = (activityId, dayKey) => {
    setMatrix((prev) => ({
      ...prev,
      activities: prev?.activities?.map((a) =>
        a._id === activityId
          ? { ...a, days: { ...a.days, [dayKey]: !a.days?.[dayKey] } }
          : a
      ),
    }));
  };

  // ── Derived data ─────────────────────────────────────────
  const activeActivities = (matrix?.activities || []).filter((a) => a.status === "active");

  // ── Loading / Empty state ────────────────────────────────
  if (loading) {
    return (
      <Flex align="center" justify="center" py={60}>
        <Loader color="yellow" />
      </Flex>
    );
  }

  if (!matrix) {
    return (
      <Paper p="xl" withBorder radius="md" style={{ textAlign: "center" }}>
        <Text size="lg" fw={600} mb="sm">
          No ADL Matrix
        </Text>
        <Text size="sm" color="dimmed" mb="md">
          This client doesn't have an ADL Matrix yet. Create one to track Activities of Daily Living.
        </Text>
        {!readOnly && (
          <Button color="yellow" loading={saving} onClick={handleCreate}>
            Create ADL Matrix
          </Button>
        )}
      </Paper>
    );
  }

  // ── Render ───────────────────────────────────────────────
  return (
    <Box className={classes.wrapper}>
      {/* Header */}
      <Flex align="center" justify="space-between" mb="md" wrap="wrap" gap={8}>
        <div>
          <Text size="lg" fw={600}>
            ADL Matrix — {clientName}
          </Text>
          {facilityName && (
            <Text size="sm" color="dimmed">
              {facilityName}
            </Text>
          )}
        </div>
        {!readOnly && (
          <Group>
            <Button variant="outline" color="gray" onClick={fetchMatrix}>
              Cancel
            </Button>
            <Button
              color="yellow"
              loading={saving}
              onClick={handleSave}
              styles={{ root: { color: "#000" } }}
            >
              Save matrix
            </Button>
          </Group>
        )}
      </Flex>

      {/* Matrix table */}
      <Paper withBorder radius="md" style={{ overflowX: "auto" }}>
        <Table className={classes.table} fontSize="sm">
          <thead>
            <tr>
              <th style={{ width: 180 }}>Activity</th>
              <th style={{ width: 80 }}>Code</th>
              {DAY_LABELS.map((d) => (
                <th key={d} className={classes.dayCell}>
                  {d}
                </th>
              ))}
              {!readOnly && <th style={{ width: 60 }} />}
            </tr>
          </thead>
          <tbody>
            {activeActivities.map((act) => (
              <tr key={act._id}>
                {/* Activity name */}
                <td>
                  {readOnly ? (
                    <Text fw={500} size="sm">
                      {act.activity}
                    </Text>
                  ) : (
                    <input
                      type="text"
                      value={act.activity}
                      onChange={(e) =>
                        updateActivity(act._id, "activity", e.target.value)
                      }
                      style={{
                        border: "none",
                        background: "transparent",
                        fontWeight: 500,
                        fontSize: 14,
                        width: "100%",
                        outline: "none",
                        fontFamily: "Inter, sans-serif",
                      }}
                    />
                  )}
                </td>

                {/* Code dropdown — native select for reliability */}
                <td>
                  {readOnly ? (
                    <CodeBadge code={act.code} />
                  ) : (
                    <NativeSelect
                      data={CODE_META.map((c) => ({
                        value: c.value,
                        label: c.value,
                      }))}
                      value={String(act.code ?? 0)}
                      onChange={(e) =>
                        updateActivity(act._id, "code", Number(e.currentTarget.value))
                      }
                      size="xs"
                      styles={{
                        input: {
                          width: 56,
                          padding: "2px 6px",
                          textAlign: "center",
                          fontWeight: 500,
                        },
                      }}
                    />
                  )}
                </td>

                {/* Day checkboxes */}
                {DAY_KEYS.map((dk) => (
                  <td key={dk} className={classes.dayCell}>
                    <div
                      className={cx(
                        classes.dayCheckbox,
                        act.days?.[dk] && classes.dayActive
                      )}
                      onClick={() => !readOnly && toggleDay(act._id, dk)}
                      style={readOnly ? { cursor: "default" } : {}}
                    >
                      {act.days?.[dk] && (
                        <svg
                          width="14"
                          height="11"
                          viewBox="0 0 14 11"
                          fill="none"
                        >
                          <path
                            d="M1 5L5 9L13 1"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  </td>
                ))}

                {/* Remove button */}
                {!readOnly && (
                  <td>
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      onClick={() => setPendingRemove(act._id)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </td>
                )}
              </tr>
            ))}
            {activeActivities.length === 0 && (
              <tr>
                <td
                  colSpan={readOnly ? 9 : 10}
                  style={{ textAlign: "center", padding: 24 }}
                >
                  <Text color="dimmed" size="sm">
                    No activities yet. Click "+ Add activity" below.
                  </Text>
                </td>
              </tr>
            )}
          </tbody>
        </Table>

        {/* Confirmation bar */}
        {pendingRemove && (
          <div className={classes.confirmBar}>
            <Text size="sm">
              Remove "
              {activeActivities.find((a) => a._id === pendingRemove)?.activity}
              " from this client's care plan?
            </Text>
            <Group>
              <Button
                size="xs"
                variant="subtle"
                color="gray"
                onClick={() => setPendingRemove(null)}
              >
                Cancel
              </Button>
              <Button
                size="xs"
                color="red"
                onClick={() => handleArchive(pendingRemove)}
              >
                Yes, remove
              </Button>
            </Group>
          </div>
        )}

        {/* Add activity */}
        {!readOnly && (
          <Box px="md" py="sm">
            <Button
              variant="subtle"
              color="yellow"
              size="xs"
              leftIcon={<IconPlus size={14} />}
              onClick={handleAddActivity}
              styles={{ root: { color: "#E6A817" } }}
            >
              Add activity
            </Button>
          </Box>
        )}
      </Paper>

      {/* Legend */}
      <Flex mt="md" gap="lg" align="center" wrap="wrap">
        <Text size="xs" fw={600} color="dimmed">
          Codes:
        </Text>
        {CODE_META.map((c) => (
          <Flex key={c.value} align="center" gap={4}>
            <Badge
              size="sm"
              styles={{ root: { background: c.bg, color: c.color } }}
            >
              {c.value}
            </Badge>
            <Text size="xs">{c.name}</Text>
          </Flex>
        ))}
      </Flex>
    </Box>
  );
};

// ── Code badge helper ────────────────────────────────────────────
const CodeBadge = ({ code }) => {
  const meta =
    CODE_META.find((c) => c.value === String(code)) || CODE_META[0];
  return (
    <Badge
      size="sm"
      styles={{ root: { background: meta.bg, color: meta.color } }}
    >
      {meta.value}
    </Badge>
  );
};

export default AdlMatrixEditor;
export { CODE_META, DAY_KEYS, DAY_LABELS, CodeBadge };
